import React from 'react'
import styles from './HomeView.module.scss'
import s from './AppSpaces.module.scss'
import { ActionTiles } from './LaunchGrid'
import { useFrostTheme } from '../theme/frost'
import WorkHome from './WorkHome'
import LifeHome from './LifeHome'
import TeachHome from './TeachHome'
import { isOwnerRole, isTeamRole } from '../util/roles'

import ForumIcon from '@mui/icons-material/Forum'
import WorkIcon from '@mui/icons-material/Work'
import SchoolIcon from '@mui/icons-material/School'
import SpaParkIcon from '@mui/icons-material/Spa'
import CloudIcon from '@mui/icons-material/Cloud'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'

// The site is several apps in one: Communicate (the languages), Teach (your
// students, for anyone who has some), Work (projects, tasks, media, notes) and
// Life (journal, inspiration, what you have). Who gets which follows their
// account — the owner all four; the team Communicate and Work, and Teach if
// they teach; a teacher Communicate and Teach; a student Communicate alone
// (and never sees a choice).

export const APPS = {
  communicate: { name: 'Communicate', blurb: 'Your languages — lessons, practice, homework and review', Icon: ForumIcon },
  teach: { name: 'Teach', blurb: 'Your students — their progress, homework to set, work to give feedback on', Icon: SchoolIcon },
  work: { name: 'Work', blurb: 'Projects, tasks, media and notes', Icon: WorkIcon },
  life: { name: 'Life', blurb: 'Your journal, inspiration, and what you watch and read', Icon: SpaParkIcon },
}

export const appsFor = (role, teaches = false) => {
  if (isOwnerRole(role)) return ['communicate', 'teach', 'work', 'life']
  const apps = ['communicate']
  if (teaches) apps.push('teach')
  if (isTeamRole(role)) apps.push('work')
  return apps
}

// Teach's tools: the registry's teaching entries (they also stay in Communicate).
const TEACH_TOOLS = ['homework', 'importLesson', 'aiLesson', 'addMeetingDoc', 'meetings']

// Which registry groups belong to which app (AppShell's `actions`).
const GROUP_APP = { Work: 'work', Life: 'life' }
export const appOfAction = (a) => GROUP_APP[a.group] || 'communicate'

const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening' }

/** Communicate · Work · Life — the one you're in in full contrast. */
export function AppSwitch({ apps, app, onPick }) {
  if (!apps || apps.length < 2) return null
  return (
    <div className={s.switch} role="tablist" aria-label="Apps">
      {apps.map(id => (
        <button key={id} role="tab" aria-selected={id === app} className={`${s.switchBtn} ${id === app ? s.switchOn : ''}`} onClick={() => onPick(id)}>
          {APPS[id].name}
        </button>
      ))}
    </div>
  )
}

/** At the start: where to go today. */
export function AppChooser({ apps, firstName, onPick, loading = false }) {
  const [theme] = useFrostTheme()
  return (
    <div className={`${styles.home} ${styles[theme]} ${s.chooser}`}>
      <div className={s.chooserInner}>
        <div className={styles.brand}>{greeting()}{firstName ? `, ${firstName}` : ''}.</div>
        {/* Held in place while loading, so nothing jumps when the cards arrive. */}
        <h1 className={styles.greet} style={loading ? { visibility: 'hidden' } : undefined}>Where to?</h1>
        <div className={s.apps} aria-busy={loading}>
          {/* Still finding out which apps are yours: quiet placeholders in their place. */}
          {loading && [0, 1, 2].map(i => <div key={i} className={`${s.appCard} ${s.appCardLoading}`} aria-hidden />)}
          {apps.map((id, i) => {
            const { name, blurb, Icon } = APPS[id]
            return (
              <button key={id} className={s.appCard} onClick={() => onPick(id)} autoFocus={i === 0}>
                <span className={s.appIcon}><Icon /></span>
                <span className={s.appName}>{name}</span>
                <span className={s.appBlurb}>{blurb}</span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/** Work's and Life's home: a dashboard of what's going on, then the tools. */
export function SpaceHome({ app, apps, onSwitch, actions, firstName, onShowCloud }) {
  const [theme, toggleTheme] = useFrostTheme()
  const mine = app === 'teach'
    ? TEACH_TOOLS.map(id => actions.find(a => a.id === id)).filter(Boolean)
    : actions.filter(a => appOfAction(a) === app)
  // A card on the dashboard opens one of the app's tools, by its id.
  const run = (id) => { const a = actions.find(x => x.id === id); if (a && a.onClick) a.onClick() }
  return (
    <div className={`${styles.home} ${styles[theme]}`}>
      <div className={styles.inner}>
        <div className={styles.top}>
          <div>
            <div className={styles.brand}>{APPS[app].name}</div>
            <h1 className={styles.greet}>{greeting()}{firstName ? `, ${firstName}` : ''}.</h1>
          </div>
          <div className={styles.topRight}>
            <AppSwitch apps={apps} app={app} onPick={onSwitch} />
            <button className={styles.iconBtn} onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} aria-label="Toggle light or dark mode">
              {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </button>
            {onShowCloud && <button className={styles.cloudBtn} onClick={onShowCloud}><CloudIcon fontSize="small" /> Word cloud</button>}
          </div>
        </div>
        {app === 'work' && <WorkHome run={run} />}
        {app === 'life' && <LifeHome run={run} />}
        {app === 'teach' && <TeachHome run={run} />}
        <section className={`${styles.card} ${s.tools}`}>
          <div className={styles.cardHead}><span className={styles.cardLabel}>Tools</span></div>
          <div className={s.bigTiles}>
            <ActionTiles actions={mine.map(a => ({ ...a, group: '' }))} onPick={(a) => a.onClick && a.onClick()} animate={false} mono />
          </div>
        </section>
      </div>
    </div>
  )
}
